"use client";

import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  PropsWithChildren,
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef
} from "react";
import { useAppContext } from "@/context/AppContext";
import {
  GetAfsluiterResponse,
  SamenvattingResponse,
  Stap,
  StappenplanResponse,
  SubvraagResponse,
  TheorieGroep,
  VraagIdentifier
} from "@/services/api_types";
import { WisWizAPIError } from "@/services/api";
import { Sectie } from "@/lib/types";
import { sortTheorieGroepen } from "@/lib/utils";
import { useShareLink } from "../hooks/useShareLink";
import { logoutEvent } from "@/lib/logoutevent";
import { useSearchParams } from "next/navigation";
import useNativeBookmark from "@/hooks/useNativeBookmark";
import { useContentSelectieContext } from "./ContentSelectieContext";

const MAX_SAMENVATTING_ATTEMPTS = 3;

type LibraryContext = {
  showGuidedExperience: boolean;
  setShowGuidedExperience: Dispatch<SetStateAction<boolean>>;

  currentSectie: Sectie | undefined;
  setCurrentSectie: Dispatch<SetStateAction<Sectie | undefined>>;
  currentVraag: VraagIdentifier | undefined;
  setCurrentVraag: Dispatch<SetStateAction<VraagIdentifier | undefined>>;
  currentSubvraag: SubvraagResponse | undefined;
  setCurrentSubvraag: Dispatch<SetStateAction<SubvraagResponse | undefined>>;
  currentStappenplan: StappenplanResponse | undefined;
  setCurrentStappenplan: Dispatch<
    SetStateAction<StappenplanResponse | undefined>
  >;
  currentStap: Stap | undefined;
  setCurrentStap: Dispatch<SetStateAction<Stap | undefined>>;

  isCurrentSubvraagUnderConstruction: boolean;

  showHint: boolean;
  setShowHint: Dispatch<SetStateAction<boolean>>;

  lockedModalOpen: boolean;
  setLockedModalOpen: Dispatch<SetStateAction<boolean>>;

  stapNummer: number;
  setStapNummer: Dispatch<SetStateAction<number>>;

  shared: boolean;
  setShared: Dispatch<SetStateAction<boolean>>;

  shareLink: string | undefined;
  setShareLink: Dispatch<SetStateAction<string | undefined>>;

  fetchShareLink: (vraagId: number) => Promise<void>;

  sideMenuExpanded: boolean;
  setSideMenuExpanded: Dispatch<SetStateAction<boolean>>;

  sideMenuExpandedWidth: number;

  deselectChapter: () => void;
  clearContentSelection: () => void;

  paymentRequired: boolean | null;
  setPaymentRequired: Dispatch<SetStateAction<boolean | null>>;
  fetchError: boolean;
  setFetchError: Dispatch<SetStateAction<boolean>>;
  fetchErrorMessage: string | undefined;
  setFetchErrorMessage: Dispatch<SetStateAction<string | undefined>>;
  rateLimited: boolean;
  setRateLimited: Dispatch<SetStateAction<boolean>>;
  nextAccessDate: Date | undefined;
  setNextAccessDate: Dispatch<SetStateAction<Date | undefined>>;

  shareToken: string | undefined;
  searchParamsProcessed: boolean;

  isOefentoets: boolean;
  // True only for the new "WisWiz Oefentoets {N}" pipeline output: vraag.tekst
  // is then a self-contained HTML fragment and the iframe renderer is used.
  isOefentoetsHtml: boolean;

  theorieGroepen: TheorieGroep[];
  samenvattingen: Map<number, SamenvattingResponse>;
  failedSamenvattingen: Set<number>;
  rateLimitedSamenvattingen: Set<number>;
  loadingSamenvattingIds: Set<number>;
  ensureSamenvatting: (groepId: number) => Promise<void>;

  afsluiter: GetAfsluiterResponse | undefined;
  setAfsluiter: Dispatch<SetStateAction<GetAfsluiterResponse | undefined>>;
};

const LibraryContext = createContext<LibraryContext | null>(null);

export const useLibraryContext = () => {
  const context = useContext(LibraryContext);
  if (context === null) {
    throw Error("No LibraryContext available");
  }
  return context;
};

export const useOptionalLibraryContext = () => {
  const context = useContext(LibraryContext);
  return context;
};

export const LibraryContextProvider = ({ children }: PropsWithChildren) => {
  const { api, currentModus } = useAppContext();
  const { deselectHoofdstuk, currentHoofdstuk } = useContentSelectieContext();

  const [stapNummer, setStapNummer] = useState<number>(0);
  const [showHint, setShowHint] = useState<boolean>(false);

  const [showGuidedExperience, setShowGuidedExperience] = useState(true);

  const [paymentRequired, setPaymentRequired] = useState<boolean | null>(null);
  const [fetchError, setFetchError] = useState<boolean>(false);
  const [fetchErrorMessage, setFetchErrorMessage] = useState<string>();
  const [rateLimited, setRateLimited] = useState<boolean>(false);
  const [nextAccessDate, setNextAccessDate] = useState<Date>();
  const [searchParamsProcessed, setSearchParamsProcessed] = useState(false);

  const [currentSectie, setCurrentSectie] = useState<Sectie>();
  const [currentVraag, setCurrentVraag] = useState<VraagIdentifier>();
  const [currentSubvraag, setCurrentSubvraag] = useState<SubvraagResponse>();
  const [currentStappenplan, setCurrentStappenplan] =
    useState<StappenplanResponse>();
  const [currentStap, setCurrentStap] = useState<Stap>();
  const [isOefentoets, setIsOefentoets] = useState<boolean>(false);
  const [isOefentoetsHtml, setIsOefentoetsHtml] = useState<boolean>(false);
  const [afsluiter, setAfsluiter] = useState<GetAfsluiterResponse>();

  // Expand sidebar when leaving huiswerk mode
  useEffect(() => {
    if (currentModus !== "huiswerk") {
      setSideMenuExpanded(true);
    }
  }, [currentModus]);

  const [theorieGroepen, setTheorieGroepen] = useState<TheorieGroep[]>([]);
  // Two parallel samenvatting stores live in the app: this one (regular
  // mode) and HuiswerkModusContext (huiswerk mode). We deliberately do NOT
  // merge them because the two flows reset on different boundaries. Cost we
  // accept: a mode switch within the same hoofdstuk re-fetches summaries.
  const [samenvattingen, setSamenvattingen] = useState<
    Map<number, SamenvattingResponse>
  >(new Map());
  const [failedSamenvattingen, setFailedSamenvattingen] = useState<Set<number>>(
    new Set()
  );
  const [rateLimitedSamenvattingen, setRateLimitedSamenvattingen] = useState<
    Set<number>
  >(new Set());
  const [loadingSamenvattingIds, setLoadingSamenvattingIds] = useState<
    Set<number>
  >(new Set());
  const loadedSamenvattingIdsRef = useRef<Set<number>>(new Set());
  const inFlightSamenvattingIdsRef = useRef<Set<number>>(new Set());
  // Per-groep attempt counter so a flaky OpenAI pipeline can't be retried
  // indefinitely by page-level effects re-firing on remount. Capped at
  // MAX_SAMENVATTING_ATTEMPTS per hoofdstuk session; reset on hoofdstuk change.
  const samenvattingAttemptsRef = useRef<Map<number, number>>(new Map());
  const samenvattingScopeRef = useRef(0);

  useEffect(() => {
    samenvattingScopeRef.current += 1;
    loadedSamenvattingIdsRef.current = new Set();
    inFlightSamenvattingIdsRef.current = new Set();
    samenvattingAttemptsRef.current = new Map();

    if (!currentHoofdstuk) {
      setTheorieGroepen([]);
      setSamenvattingen(new Map());
      setFailedSamenvattingen(new Set());
      setRateLimitedSamenvattingen(new Set());
      setLoadingSamenvattingIds(new Set());
      return;
    }
    setSamenvattingen(new Map());
    setFailedSamenvattingen(new Set());
    setRateLimitedSamenvattingen(new Set());
    setLoadingSamenvattingIds(new Set());

    const allVraagIds = currentHoofdstuk.secties.flatMap((s) =>
      s.vragen.map((v) => v.id)
    );
    if (allVraagIds.length === 0) {
      setTheorieGroepen([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const groepen = await api.groepeerVragen(allVraagIds);
        if (!cancelled)
          setTheorieGroepen(sortTheorieGroepen(groepen, currentHoofdstuk));
      } catch {
        if (!cancelled) setTheorieGroepen([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, currentHoofdstuk]);

  const ensureSamenvatting = useCallback(
    async (groepId: number) => {
      if (loadedSamenvattingIdsRef.current.has(groepId)) return;
      if (inFlightSamenvattingIdsRef.current.has(groepId)) return;
      const attempts = samenvattingAttemptsRef.current.get(groepId) ?? 0;
      if (attempts >= MAX_SAMENVATTING_ATTEMPTS) return;

      inFlightSamenvattingIdsRef.current.add(groepId);
      samenvattingAttemptsRef.current.set(groepId, attempts + 1);
      const scope = samenvattingScopeRef.current;

      setFailedSamenvattingen((prev) => {
        if (!prev.has(groepId)) return prev;
        const next = new Set(prev);
        next.delete(groepId);
        return next;
      });
      setRateLimitedSamenvattingen((prev) => {
        if (!prev.has(groepId)) return prev;
        const next = new Set(prev);
        next.delete(groepId);
        return next;
      });
      setLoadingSamenvattingIds((prev) => new Set(prev).add(groepId));

      try {
        const result = await api.hwmSamenvatting(groepId);
        if (scope !== samenvattingScopeRef.current) return;

        loadedSamenvattingIdsRef.current.add(groepId);
        setSamenvattingen((prev) => new Map(prev).set(groepId, result));
      } catch (err) {
        if (scope !== samenvattingScopeRef.current) return;

        if (err instanceof WisWizAPIError && err.statusCode === 429) {
          setRateLimitedSamenvattingen((prev) => new Set(prev).add(groepId));
        } else {
          setFailedSamenvattingen((prev) => new Set(prev).add(groepId));
        }
      } finally {
        if (scope !== samenvattingScopeRef.current) return;

        inFlightSamenvattingIdsRef.current.delete(groepId);
        setLoadingSamenvattingIds((prev) => {
          const next = new Set(prev);
          next.delete(groepId);
          return next;
        });
      }
    },
    [api]
  );

  const [
    isCurrentSubvraagUnderConstruction,
    setIsCurrentSubvraagUnderConstruction
  ] = useState<boolean>(false);

  const [lockedModalOpen, setLockedModalOpen] = useState<boolean>(false);

  const [shared, setShared] = useState<boolean>(false);
  const [shareLink, setShareLink] = useState<string | undefined>();
  const [sideMenuExpanded, setSideMenuExpanded] = useState<boolean>(true);

  const searchParams = useSearchParams();
  // Keep share access scoped to the current URL. A viewer can arrive through a
  // share link and then navigate to normal library content without `?share=...`;
  // in that case the old token must not keep authorizing or poisoning requests
  // for unrelated questions.
  const shareToken = searchParams.get("share") ?? undefined;

  useEffect(() => {
    setSearchParamsProcessed(true);
    setShared(shareToken !== undefined);
  }, [shareToken]);

  // Calculate default expanded width (1/8th of screen)
  const getDefaultExpandedWidth = () => {
    if (typeof window !== "undefined") {
      const eighthOfScreen = Math.floor(window.innerWidth / 8);
      return Math.min(400, Math.max(280, eighthOfScreen));
    }
    return 280; // Fallback for SSR
  };

  // Load saved expanded width from localStorage or use default
  const getInitialExpandedWidth = () => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("sideMenuExpandedWidth");
      if (saved) {
        const savedWidth = parseInt(saved);
        // Ensure saved width respects current screen constraints
        const maxAllowed = getDefaultExpandedWidth();
        return Math.min(savedWidth, maxAllowed);
      }
    }
    return getDefaultExpandedWidth();
  };

  const sideMenuExpandedWidth = getInitialExpandedWidth();

  const clearContentSelection = useCallback(() => {
    setCurrentVraag(undefined);
    setCurrentSubvraag(undefined);
    setCurrentStappenplan(undefined);
    setAfsluiter(undefined);
    setStapNummer(0);
  }, []);

  const deselectChapter = useCallback(() => {
    clearContentSelection();
    deselectHoofdstuk();
  }, [clearContentSelection, deselectHoofdstuk]);

  useEffect(() => {
    const handleLogout = () => {
      setCurrentVraag(undefined);
      setCurrentSubvraag(undefined);
      setCurrentStappenplan(undefined);
      setAfsluiter(undefined);
      setStapNummer(0);
    };
    logoutEvent.addEventListener("logout", handleLogout);
    return () => logoutEvent.removeEventListener("logout", handleLogout);
  }, []);

  const fetchShareLink = useShareLink({ api, setShareLink });

  // Under construction check
  useEffect(() => {
    if (currentSubvraag) {
      if (
        currentSubvraag.hint.tekst ===
        "De uitleg van deze subvraag kan nog duidelijker en wordt daarom verbouwd."
      ) {
        setIsCurrentSubvraagUnderConstruction(true);
      } else {
        setIsCurrentSubvraagUnderConstruction(false);
      }
    } else {
      setIsCurrentSubvraagUnderConstruction(false);
    }
  }, [currentSubvraag]);

  useEffect(() => {
    setIsOefentoets(currentSectie?.is_oefentoets ?? false);
    setIsOefentoetsHtml(
      (currentSectie?.is_oefentoets ?? false) &&
        (currentSectie?.is_html ?? false)
    );
  }, [currentSectie]);

  useNativeBookmark(currentVraag, currentSubvraag);

  const providerValue = useMemo(() => {
    return {
      stapNummer,
      setStapNummer,
      showHint,
      setShowHint,
      showGuidedExperience,
      setShowGuidedExperience,
      currentSectie,
      setCurrentSectie,
      currentVraag,
      setCurrentVraag,
      currentSubvraag,
      setCurrentSubvraag,
      currentStappenplan,
      setCurrentStappenplan,
      isCurrentSubvraagUnderConstruction,

      lockedModalOpen,
      setLockedModalOpen,

      currentStap,
      setCurrentStap,
      shared,
      setShared,
      shareLink,
      setShareLink,
      fetchShareLink,
      sideMenuExpanded,
      setSideMenuExpanded,
      sideMenuExpandedWidth,
      deselectChapter,
      clearContentSelection,
      paymentRequired,
      setPaymentRequired,
      fetchError,
      setFetchError,
      fetchErrorMessage,
      setFetchErrorMessage,
      rateLimited,
      setRateLimited,
      nextAccessDate,
      setNextAccessDate,
      shareToken,
      searchParamsProcessed,
      isOefentoets,
      isOefentoetsHtml,
      theorieGroepen,
      samenvattingen,
      failedSamenvattingen,
      rateLimitedSamenvattingen,
      loadingSamenvattingIds,
      ensureSamenvatting,

      afsluiter,
      setAfsluiter
    };
  }, [
    stapNummer,
    showHint,
    showGuidedExperience,
    currentSectie,
    currentVraag,
    currentSubvraag,
    currentStappenplan,
    currentStap,
    isCurrentSubvraagUnderConstruction,
    lockedModalOpen,
    shared,
    shareLink,
    sideMenuExpanded,
    setSideMenuExpanded,
    fetchShareLink,
    sideMenuExpandedWidth,

    deselectChapter,
    clearContentSelection,
    paymentRequired,
    fetchError,
    fetchErrorMessage,
    rateLimited,
    nextAccessDate,
    shareToken,
    searchParamsProcessed,
    isOefentoets,
    isOefentoetsHtml,
    theorieGroepen,
    samenvattingen,
    failedSamenvattingen,
    rateLimitedSamenvattingen,
    loadingSamenvattingIds,
    ensureSamenvatting,

    afsluiter
  ]);

  return (
    <LibraryContext.Provider value={providerValue}>
      {children}
    </LibraryContext.Provider>
  );
};
